import type { CreatePageStore } from "@/stores/createPageStore";
import type { DesignSpec } from "@/types/designSpec";
import type { DesignSpecFieldChange } from "@/types/generatedImage";
import {
  createDesignSpecSnapshot,
  diffDesignSpecFields,
} from "@/lib/agent/designSpecSnapshot";
import {
  evaluateAgentActionReadiness,
  hasBackUploadReference,
  hasConfirmedBackDesignDirection,
  hasExplicitBackDesignRequest,
} from "@/lib/agent/actionReadinessGuards";
import { compileCurrentDesignSpec } from "@/lib/agent/briefForm";
import {
  getSideRelevantDesignChanges,
  getPresentableDesignChanges,
  projectPreviewFreshness,
  projectSavedBackStaleness,
} from "./previewFreshness";
import { restoreDesignDraft } from "./restoreDesignDraft";

export type DesignRenderSide = "front" | "back";

/** Execution details, never a replacement for the action's auth/credit gates. */
export interface DesignRenderExecutionOptions {
  side?: DesignRenderSide;
  /** An intermediate image must not start an agent turn during a paid batch. */
  suppressAgentReview?: boolean;
  /** Refresh an existing Back using the latest saved Front as its source. */
  regenerateBackFromFront?: boolean;
}

export interface ApplyDesignChangeStep {
  side: DesignRenderSide;
  mode: "modify" | "generate";
  changes: DesignSpecFieldChange[];
}

export interface DesignChangesInput {
  form: Pick<CreatePageStore["form"], "data">;
  agent: Pick<CreatePageStore["agent"], "designBrief" | "phase">;
  generation: {
    images: Record<
      DesignRenderSide,
      Pick<
        CreatePageStore["generation"]["images"]["front"],
        "url" | "path" | "designSpec"
      >
    >;
  };
}

const BACK_DESIGN_FIELDS = ["backPrompt", "backStyle", "closure"] as const;

/** A saved back description is context, not an instruction to buy a preview. */
function hasPendingBackRequest(
  state: DesignChangesInput,
  draft: DesignSpec | null
) {
  if (
    !hasExplicitBackDesignRequest(
      state.form.data,
      hasConfirmedBackDesignDirection(draft)
    )
  )
    return false;
  if (
    state.form.data.generateBack === true ||
    state.agent.phase === "back_design" ||
    state.agent.phase === "back_review" ||
    hasBackUploadReference(state.form.data)
  )
    return true;

  const baseline = state.generation.images.front.designSpec;
  if (!baseline || !draft) return false;
  // Compare back-only edits against the contract captured with the front.
  // Shared front edits and inherited/unchanged back details do not add a side.
  return diffDesignSpecFields(baseline, draft).some(change => {
    if (!BACK_DESIGN_FIELDS.some(field => change.path === `visual.${field}`))
      return false;
    const provenance = draft.provenance[change.path];
    return Boolean(
      change.draftValue &&
      provenance?.confirmed &&
      provenance.source !== "inferred"
    );
  });
}

export function isDesignWorkspaceBusy(state: CreatePageStore): boolean {
  return Boolean(
    state.ticket.designSubmitted ||
    state.generation.progress.loading ||
    state.ui.modification.isModifying ||
    state.generation.images.isSelectingVariation ||
    state.agent.conversation.isTyping ||
    state.agent.conversation.presetsLoading ||
    state.agent.activeRequestController
  );
}

/** Both the visible cost and execution use this same committed draft plan. */
export function planDesignChanges(state: DesignChangesInput) {
  const images = state.generation.images;
  const draft = createDesignSpecSnapshot(
    compileCurrentDesignSpec(state.agent.designBrief, state.form.data, {
      hasFrontImage: Boolean(images.front.url),
      hasBackImage: Boolean(images.back.url),
    })
  );
  const candidateSteps: ApplyDesignChangeStep[] = [];
  const bySide = { front: [], back: [] } as Record<
    DesignRenderSide,
    DesignSpecFieldChange[]
  >;
  const unavailableReasons: Record<DesignRenderSide, string | null> = {
    front: null,
    back: null,
  };
  const requestedBack = hasPendingBackRequest(state, draft);
  const savedBack = projectSavedBackStaleness(images);
  const savedBackChanges = savedBack.changes;

  for (const side of ["front", "back"] as const) {
    const image = images[side];
    const freshness = projectPreviewFreshness({
      side,
      hasImage: Boolean(image.url),
      renderedSpec: image.designSpec,
      draftSpec: draft,
    });
    const legacyBaseline = state.form.data.legacyPreviewEditBaselines?.[side];
    const legacyEditMatchesImage = Boolean(
      image.url &&
      !image.designSpec &&
      legacyBaseline?.imageKey === (image.path || image.url)
    );
    const legacyChanges =
      legacyEditMatchesImage && legacyBaseline?.draft && draft
        ? getSideRelevantDesignChanges(
            diffDesignSpecFields(legacyBaseline.draft, draft),
            side
          ).map(change => ({ ...change, renderedValue: undefined }))
        : [];
    const unknownEditPending =
      legacyEditMatchesImage &&
      (!legacyBaseline?.draft || !draft || legacyChanges.length > 0);
    const sideChanges =
      freshness.freshness === "changes_pending"
        ? freshness.changes
        : legacyChanges;
    bySide[side] = sideChanges;
    // Differences already committed to the new Front are a reminder that the
    // saved Back can be refreshed later, not permission to charge for it now.
    // A separate Back edit remains actionable and includes those shared
    // differences in its single Back render.
    const onlySavedFrontDifferences =
      side === "back" &&
      sideChanges.length > 0 &&
      getPresentableDesignChanges(sideChanges).every(change =>
        savedBackChanges.some(
          saved =>
            saved.path === change.path &&
            JSON.stringify(saved.draftValue) ===
              JSON.stringify(change.draftValue)
        )
      );
    if (onlySavedFrontDifferences) bySide.back = [];
    if (
      (freshness.freshness === "changes_pending" || unknownEditPending) &&
      !onlySavedFrontDifferences
    ) {
      // An older Back needs the latest Front as its source. If the customer
      // also edited Back details, include them in that one regenerated image.
      candidateSteps.push({
        side,
        mode: side === "back" && savedBack.stale ? "generate" : "modify",
        changes: sideChanges,
      });
      if (!image.path?.trim()) {
        unavailableReasons[side] = "Save the preview before applying changes.";
      }
      if (side === "back" && savedBack.stale && !images.front.path?.trim()) {
        unavailableReasons.back =
          "Save the front preview before updating the back.";
      }
    } else if (!image.url && (side === "front" || requestedBack)) {
      // A new back has no rendered baseline. State what it will create instead
      // of showing "Changes to back" with only "Front saved" underneath.
      const changes: DesignSpecFieldChange[] =
        side === "back" && draft
          ? BACK_DESIGN_FIELDS.flatMap(field =>
              draft.visual[field]
                ? [
                    {
                      path: `visual.${field}`,
                      renderedValue: undefined,
                      draftValue: draft.visual[field],
                    },
                  ]
                : []
            )
          : [];
      bySide[side] = changes;
      candidateSteps.push({ side, mode: "generate", changes });
      const readiness = evaluateAgentActionReadiness({
        actionId: `generate_dress_${side}`,
        phase: state.agent.phase,
        formState: state.form.data,
        // When both are requested, back readiness is evaluated after front.
        hasFrontImage: Boolean(images.front.url) || side === "back",
        hasBackImage: Boolean(images.back.url),
        frontApproved: state.form.data.frontApproved === true,
        hasBackDesignDirection: hasConfirmedBackDesignDirection(draft),
      });
      if (!readiness.allowed) unavailableReasons[side] ??= readiness.message;
      if (side === "back" && images.front.url && !images.front.path?.trim()) {
        unavailableReasons[side] =
          "Save the front preview before creating the back.";
      }
    }
  }
  // One explicit commit buys one image. When both sides need new previews,
  // commit Front first and keep the Back request for a later decision.
  const steps = candidateSteps.slice(0, 1);
  const deferredSides = candidateSteps.slice(1).map(step => step.side);
  const changes = [...bySide.front, ...bySide.back].filter(
    (change, index, all) =>
      all.findIndex(candidate => candidate.path === change.path) === index
  );
  return {
    draft: draft as DesignSpec | null,
    steps,
    changes,
    frontChanges: bySide.front,
    backChanges: bySide.back,
    pendingSides: steps.map(step => step.side),
    deferredSides,
    creditCost: steps.length,
    hasPendingChanges: Boolean(images.front.url) && steps.length > 0,
    isInitialGeneration: !images.front.url,
    unavailableReason: draft
      ? steps.length
        ? unavailableReasons[steps[0].side]
        : null
      : "Add your design details first.",
  };
}

/** Restore the saved dress without undoing a render that already succeeded. */
export function planDesignChangesUndo(
  state: DesignChangesInput,
  plan = planDesignChanges(state)
) {
  const { front, back } = state.generation.images;
  if (!plan.hasPendingChanges || !front.designSpec) return null;
  if (back.url && !back.designSpec) return null;

  const saved: DesignSpec = {
    ...front.designSpec,
    visual: { ...front.designSpec.visual },
    provenance: { ...front.designSpec.provenance },
  };
  if (back.url && back.designSpec) {
    // Back-only changes may have been saved after the front. Restore those
    // from the back image, retaining shared details from the front image.
    for (const field of BACK_DESIGN_FIELDS) {
      saved.visual[field] = back.designSpec.visual[field];
      const path = `visual.${field}`;
      delete saved.provenance[path];
      if (back.designSpec.provenance[path])
        saved.provenance[path] = { ...back.designSpec.provenance[path] };
    }
    const isBackReference = (reference: DesignSpec["references"][number]) =>
      reference.side === "back" || reference.role === "back";
    if (
      JSON.stringify(saved.references) !==
      JSON.stringify(back.designSpec.references)
    )
      saved.references = [
        ...saved.references.filter(reference => !isBackReference(reference)),
        ...back.designSpec.references.filter(isBackReference),
      ];
  }

  const restored = restoreDesignDraft(saved);
  const cancelBackRequest = !back.url;
  const formData = { ...state.form.data, ...restored.formData };
  const referencesChanged = plan.changes.some(
    change => change.path === "references"
  );
  if (referencesChanged) {
    formData.uploadedFrontImages = [];
    formData.uploadedBackImages = [];
    formData.uploadedFrontImageUrls = saved.references
      .filter(reference => reference.side === "front")
      .map(reference => reference.url);
    formData.uploadedBackImageUrls = saved.references
      .filter(reference => reference.side === "back")
      .map(reference => reference.url);
    formData.uploadedFrontImageCount = formData.uploadedFrontImageUrls.length;
    formData.uploadedBackImageCount = formData.uploadedBackImageUrls.length;
  }
  if (cancelBackRequest) {
    formData.generateBack = false;
    formData.backApproved = false;
    formData.uploadedBackImages = [];
    formData.uploadedBackImageUrls = [];
    formData.uploadedBackImageCount = 0;
    formData.isBackUploadPreset = false;
    formData.isBackCustomPreset = false;
  }
  const phase = cancelBackRequest ? "front_review" : state.agent.phase;
  const restoredState: DesignChangesInput = {
    form: { data: formData },
    agent: { designBrief: restored.brief, phase },
    generation: state.generation,
  };
  // A partially successful front/back batch can have conflicting shared
  // baselines. Never claim Undo cleared changes while one preview is stale.
  if (planDesignChanges(restoredState).hasPendingChanges) return null;
  return { formData, designBrief: restored.brief, phase, saved };
}
